/** @jsxImportSource @emotion/react */

import { FC, ReactElement } from 'react'
import Typography from '@mui/material/Typography'
import { Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import arrayMutators from 'final-form-arrays'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { FormApi } from 'final-form'
import * as tableButtonsClasses from 'components/TableButtons/styles'
import { SelectField } from 'components/Select'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { useLink } from 'routes/cmsRoutes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { SchoolLocationTypeEnum } from '__generatedTypes__/globalTypes'
import * as classes from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form/styles'
import { FormValues } from './types'

const DENTISTRY_SCHOOL_LOCATIONS: Array<{ id: SchoolLocationTypeEnum; title: string }> = [
  {
    id: SchoolLocationTypeEnum.American,
    title: 'American Dental Schools',
  },
  {
    id: SchoolLocationTypeEnum.Canadian,
    title: 'Canadian Dental Schools',
  },
]

const OPTOMETRY_SCHOOL_LOCATIONS: Array<{ id: SchoolLocationTypeEnum; title: string }> = [
  {
    id: SchoolLocationTypeEnum.Optometry,
    title: 'Optometry Schools',
  },
]

export const getSchoolLocations = (isOptometry?: boolean) =>
  isOptometry ? OPTOMETRY_SCHOOL_LOCATIONS : DENTISTRY_SCHOOL_LOCATIONS

type Props = {
  platform: Platform
  initialValues?: FormValues
  onSubmit: (values: FormValues) => Promise<void>
  title: string
  createLink: string
  renderFields: (params: {
    form: FormApi<FormValues, Partial<FormValues>>
    linkParams?: URLSearchParams
  }) => ReactElement
  isOptometry?: boolean
}

export const Form: FC<Props> = ({ initialValues, onSubmit, title, createLink, renderFields, isOptometry }) => {
  const navigate = useNavigate()
  const { id: sectionId } = useParams()
  const { getLink } = useLink()

  const linkParams = sectionId ? new URLSearchParams({ sectionId }) : undefined

  return (
    <section css={classes.container}>
      <div css={classes.headerSection}>
        <Typography css={classes.header} variant="title3">
          {title}
        </Typography>
        <Link css={classes.addContentLnk} to={getLink(createLink)} target="_blank">
          <Button color="blue" size="medium">
            <AddRoundedIcon css={tableButtonsClasses.iconPlus} /> Add content
          </Button>
        </Link>
      </div>

      <FinalForm<FormValues>
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        onSubmit={onSubmit}
        render={({ handleSubmit, form, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <InputField
              css={classes.field}
              name="title"
              validate={validateRequired}
              label="Title"
              placeholder="Enter Title"
              fullWidth
            />
            <SelectField
              css={classes.field}
              name="schoolLocation"
              validate={validateRequired}
              items={getSchoolLocations(isOptometry)}
              label="School Location"
              placeholder="Choose School Location"
              fullWidth
            />
            {renderFields({ form, linkParams })}
            <div css={classes.actionButtons}>
              <Button
                variant="outlined"
                color="blue"
                size="large"
                type="button"
                css={[classes.actionButton, classes.cancelButton]}
                disabled={submitting}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button size="large" color="blue" type="submit" css={classes.actionButton} disabled={submitting}>
                {initialValues?.id ? 'Save Edits' : 'Create'}
              </Button>
            </div>
          </form>
        )}
      />
    </section>
  )
}
